import React, { useEffect, useMemo, useRef, useState } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "../lib/firebase";
import CustomerReviews from "../components/CustomerReviews";
import Hero from "../components/Hero";
import PdfDocumentSection from "../components/PdfDocumentSection";
import "./AboutPage.css";
import useIsMobile from "../hooks/useIsMobile";
import { adjustFontSize } from "../utils/typography";
import usePdfConfig from "../hooks/usePdfConfig";
import { getPdfDocumentsForSection } from "../utils/pdfDocuments";

import { CONFIG } from "../config";

/** ---- Config ---- */
const IMAGES_BASE = CONFIG.IMAGES_BASE;
const EMPTY_OBJECT = Object.freeze({});

/** ---- Helpers ---- */
const toImageSrc = (val) => {
  if (!val || typeof val !== "string") return "";
  if (/^https?:\/\//i.test(val) || /^\/\//.test(val)) return val;
  if (val.startsWith("/")) return val;
  return `${IMAGES_BASE}/${encodeURIComponent(val)}`;
};

const asArray = (v) =>
  Array.isArray(v) ? v : v && typeof v === "object" ? Object.values(v) : [];

const toNumber = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const n = Number(s.replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : null;
};

function useInViewOnce(options = { threshold: 0.2 }) {
  const refEl = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = refEl.current;
    if (!el) return;

    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        obs.disconnect();
      }
    }, options);

    obs.observe(el);
    return () => obs.disconnect();
  }, [options]);

  return [refEl, inView];
}

function useCountUp(target, startWhen, duration = 900) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!startWhen) return;

    const t = Number(target);
    if (!Number.isFinite(t)) return;

    let raf = 0;
    const start = performance.now();

    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(t * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, startWhen, duration]);

  return value;
}

const textStyleKeyMap = {
  heading: { color: "headingColor", size: "headingSize" },
  subHeading: { color: "subHeadingColor", size: "subHeadingSize" },
  content: { color: "contentColor", size: "contentSize" },
  stats: { color: "statsColor", size: "statsSize" },
};

function buildTextStyle(type, fontStyles, sectionText, useSectionText, isMobile) {
  const base = fontStyles?.[type] || {};
  const keys = textStyleKeyMap[type] || {};
  const colorValue = useSectionText
    ? sectionText?.[keys.color] || base.color
    : base.color;
  const sizeValue = useSectionText
    ? sectionText?.[keys.size] || base.fontSize
    : base.fontSize;

  return {
    fontFamily: base.fontFamily || "inherit",
    fontWeight: base.fontWeight || "inherit",
    lineHeight: base.lineHeight || "normal",
    fontSize: adjustFontSize(sizeValue || undefined, isMobile),
    color: colorValue || undefined,
  };
}

function getSectionStyles(sectionData, fontStyles, sectionColors) {
  if (!fontStyles) return {};

  const styles = {};
  const useSectionColors = sectionData?.useSectionColors !== false;
  const useSectionFontSizes = sectionData?.useSectionFontSizes === true;

  if (fontStyles.heading) styles.headingFont = fontStyles.heading;
  if (fontStyles.subHeading) styles.subHeadingFont = fontStyles.subHeading;
  if (fontStyles.content) styles.contentFont = fontStyles.content;
  if (fontStyles.button) styles.buttonFont = fontStyles.button;
  if (fontStyles.stats) styles.statsFont = fontStyles.stats;

  if (useSectionFontSizes && sectionData?.fontSizes) {
    styles.fontSizes = { ...sectionData.fontSizes };
  }

  if (useSectionColors && sectionColors) {
    styles.headingColor = sectionColors.headingColor || fontStyles.heading?.color;
    styles.subHeadingColor = sectionColors.subHeadingColor || fontStyles.subHeading?.color;
    styles.contentColor = sectionColors.contentColor || fontStyles.content?.color;
    styles.buttonTextColor = sectionColors.buttonTextColor || fontStyles.button?.color;
    styles.buttonBgColor = sectionColors.buttonBgColor;

    if (sectionColors.statsColor) styles.statsColor = sectionColors.statsColor;
    if (sectionColors.iconColor) styles.iconColor = sectionColors.iconColor;
    if (sectionColors.ratingColor) styles.ratingColor = sectionColors.ratingColor;
    if (sectionColors.reviewerNameColor) styles.reviewerNameColor = sectionColors.reviewerNameColor;
    if (sectionColors.reviewerDesignationColor)
      styles.reviewerDesignationColor = sectionColors.reviewerDesignationColor;
    if (sectionColors.numberColor) styles.numberColor = sectionColors.numberColor;
    if (sectionColors.cardBgColor) styles.cardBgColor = sectionColors.cardBgColor;
  } else {
    styles.headingColor = fontStyles.heading?.color;
    styles.subHeadingColor = fontStyles.subHeading?.color;
    styles.contentColor = fontStyles.content?.color;
    styles.buttonTextColor = fontStyles.button?.color;
    styles.buttonBgColor = fontStyles.button?.backgroundColor;
    styles.statsColor = fontStyles.stats?.color;
  }

  return styles;
}

export default function AboutPage() {
  const isMobile = useIsMobile();
  const { pdfConfig } = usePdfConfig();
  const [about, setAbout] = useState(null);
  const [homepageCfg, setHomepageCfg] = useState(null);
  const [navCfg, setNavCfg] = useState(null);
  const [aboutReady, setAboutReady] = useState(false);
  const [homepageReady, setHomepageReady] = useState(false);
  const [navReady, setNavReady] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const off1 = onValue(
      ref(db, "aboutusconfig"),
      (s) => {
        setAbout(s.val() || null);
        setAboutReady(true);
      },
      (err) => {
        console.error("aboutusconfig load error:", err);
        setLoadError("Failed to load about page data.");
        setAboutReady(true);
      }
    );
    const off2 = onValue(
      ref(db, "homepageconfig"),
      (s) => {
        setHomepageCfg(s.val() || null);
        setHomepageReady(true);
      },
      (err) => {
        console.error("homepageconfig load error:", err);
        setLoadError("Failed to load about page data.");
        setHomepageReady(true);
      }
    );
    const off3 = onValue(
      ref(db, "navconfig"),
      (s) => {
        setNavCfg(s.val() || null);
        setNavReady(true);
      },
      (err) => {
        console.error("navconfig load error:", err);
        setLoadError("Failed to load about page data.");
        setNavReady(true);
      }
    );

    return () => {
      off1();
      off2();
      off3();
    };
  }, []);

  const loading = !aboutReady || !homepageReady || !navReady;

  const pageBg = homepageCfg?.backgroundColor || "transparent";

  const fontStyles = homepageCfg?.fontStyles || null;
  const useSectionTextStyles = about?.useSectionTextStyles === true;
  const sectionTextStyles = useMemo(
    () => about?.textStyles || EMPTY_OBJECT,
    [about]
  );

  const headingStyle = useMemo(
    () =>
      buildTextStyle(
        "heading",
        fontStyles,
        sectionTextStyles,
        useSectionTextStyles,
        isMobile
      ),
    [fontStyles, sectionTextStyles, useSectionTextStyles, isMobile]
  );
  const subHeadingStyle = useMemo(
    () =>
      buildTextStyle(
        "subHeading",
        fontStyles,
        sectionTextStyles,
        useSectionTextStyles,
        isMobile
      ),
    [fontStyles, sectionTextStyles, useSectionTextStyles, isMobile]
  );
  const contentStyle = useMemo(
    () =>
      buildTextStyle(
        "content",
        fontStyles,
        sectionTextStyles,
        useSectionTextStyles,
        isMobile
      ),
    [fontStyles, sectionTextStyles, useSectionTextStyles, isMobile]
  );
  const statsStyle = useMemo(
    () =>
      buildTextStyle(
        "stats",
        fontStyles,
        sectionTextStyles,
        useSectionTextStyles,
        isMobile
      ),
    [fontStyles, sectionTextStyles, useSectionTextStyles, isMobile]
  );
  const buttonTextStyle = useMemo(() => {
    const base = fontStyles?.button || {};
    return {
      fontFamily: base.fontFamily || "inherit",
      fontWeight: base.fontWeight || "inherit",
      fontSize: adjustFontSize(base.fontSize || undefined, isMobile),
      lineHeight: base.lineHeight || undefined,
    };
  }, [fontStyles, isMobile]);

  const ctaHeadingStyle = useMemo(
    () => ({
      ...headingStyle,
      color: "#ffffff",
    }),
    [headingStyle]
  );

  const ctaCopyStyle = useMemo(
    () => ({
      ...contentStyle,
      color: "#ffffff",
    }),
    [contentStyle]
  );

  const whyKickerStyle = useMemo(
    () => ({
      ...subHeadingStyle,
      color: "#c58b4f",
    }),
    [subHeadingStyle]
  );

  const whyHeadingStyle = useMemo(
    () => ({
      ...headingStyle,
      color: "#ffffff",
    }),
    [headingStyle]
  );

  const whyCopyStyle = useMemo(
    () => ({
      ...contentStyle,
      color: "rgba(246, 242, 236, 0.88)",
    }),
    [contentStyle]
  );

  const whyCardTitleStyle = useMemo(
    () => ({
      ...subHeadingStyle,
      color: "#1f1b17",
    }),
    [subHeadingStyle]
  );

  const whyCardTextStyle = useMemo(
    () => ({
      ...contentStyle,
      color: "#6b5f54",
    }),
    [contentStyle]
  );

  // hero slides (about config only)
  const aboutHeroSlides = useMemo(() => {
    if (!about) return [];
    if (about?.useSlideshow) {
      return asArray(about?.heroSlides)
        .map((s) => ({
          image: s?.image || "",
          phoneImage: s?.phoneImage || "",
          title: s?.title || "",
          content: s?.subtitle || "",
        }))
        .filter((s) => s.image || s.phoneImage);
    }

    const singleImage = about?.desktopImage || about?.phoneImage || "";
    if (!singleImage) return [];

    return [
      {
        image: about?.desktopImage || about?.phoneImage || "",
        phoneImage: about?.phoneImage || "",
        title: about?.heroTitle || "",
        content: about?.heroSubtitle || "",
        buttonname: about?.heroButtonText || "",
        buttonlink: about?.heroButtonLink || "",
      },
    ];
  }, [about]);

  const heroAutoPlay = about?.heroAutoPlay !== false;
  const heroIntervalMs = Number(about?.heroIntervalMs) || 5000;

  // content
  const description = about?.description || "";
  const paragraphs = useMemo(
    () =>
      description
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean),
    [description]
  );

  const homepageAbout = homepageCfg?.aboutUs || {};
  const introKicker = about?.introKicker || "";
  const introHeading =
    about?.introHeading ||
    homepageAbout?.title ||
    "";
  const introLead =
    paragraphs[0] ||
    homepageAbout?.description ||
    "";
  const introBody = paragraphs.length > 1 ? paragraphs.slice(1) : [];

  const normalizeText = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

  const kickerNormalized = normalizeText(introKicker);
  const headingNormalized = normalizeText(introHeading);
  const showIntroKicker =
    introKicker &&
    kickerNormalized &&
    (!headingNormalized || !headingNormalized.includes(kickerNormalized));

  const stats = {
    years: homepageAbout?.yearsExperience || "",
    projects: homepageAbout?.projectsCompleted || "",
  };

  const [statsRef, statsInView] = useInViewOnce({ threshold: 0.2 });
  const yearsNum = toNumber(stats.years);
  const projectsNum = toNumber(stats.projects);
  const yearsAnimated = useCountUp(yearsNum ?? 0, statsInView, 3500);
  const projectsAnimated = useCountUp(projectsNum ?? 0, statsInView, 4500);

  const primaryCta = {
    text: homepageAbout?.buttonText || "",
    link: homepageAbout?.buttonLink || "",
  };

  const whyHeading =
    about?.whyHeading || "";
  const whyCopy =
    about?.whyCopy ||
    paragraphs[1] ||
    "";
  const whyButton = {
    text: about?.whyButtonText || "",
    link: about?.whyButtonLink || "",
  };

  const whyItems = useMemo(() => {
    const raw = asArray(about?.highlights || about?.whyItems);
    const normalized = raw
      .map((item) => ({
        title: item?.title || item?.name || "",
        description: item?.description || item?.text || "",
      }))
      .filter((item) => item.title || item.description);
    return normalized;
  }, [about]);

  const ctaTitle =
    about?.ctaTitle || "";
  const ctaCopy =
    about?.ctaCopy ||
    "";
  const ctaButton = {
    text: about?.ctaButtonText || "",
    link: about?.ctaButtonLink || "",
  };

  const team = useMemo(
    () =>
      asArray(about?.team).map((t) => ({
        name: t?.name || "",
        description: t?.description || "",
        image: t?.image || "",
      })),
    [about]
  );

  const certificates = useMemo(
    () =>
      asArray(about?.certificates).map((c) => ({
        image: c?.image || "",
        description: c?.description || "",
      })),
    [about]
  );

  const hasStats = Boolean(stats.years || stats.projects);
  const hasIntroContent = Boolean(
    showIntroKicker ||
      introHeading ||
      introLead ||
      introBody.length ||
      primaryCta.text ||
      hasStats
  );
  const showWhySection = Boolean(
    whyHeading || whyCopy || whyButton.text || whyItems.length
  );
  const showCtaSection = Boolean(ctaTitle || ctaCopy || ctaButton.text);
  const aboutPdfDocuments = useMemo(
    () => getPdfDocumentsForSection(pdfConfig?.documents, "about"),
    [pdfConfig?.documents]
  );

  const customerReviewColors = useMemo(
    () => homepageCfg?.customerReviewColors || EMPTY_OBJECT,
    [homepageCfg]
  );
  const customerReviewCfg = useMemo(() => {
    const cr = homepageCfg?.customerreview;
    if (!cr || typeof cr !== "object") return null;

    const image = toImageSrc(cr.image);
    const reviews = Array.isArray(cr.reviews) ? cr.reviews : [];

    if (!image || !reviews.length) return null;

    return {
      backgroundColor: cr.backgroundColor,
      overlayOpacity: cr.overlayOpacity,
      image,
      reviews,
      sectionTitle: cr.sectionTitle || "",
      sectionDescription: cr.sectionDescription || "",
      sectionStyles: getSectionStyles(cr, fontStyles, customerReviewColors),
    };
  }, [homepageCfg, fontStyles, customerReviewColors]);

  // Animate sections on scroll (alternate left/right)
  useEffect(() => {
    if (loading) return;
    if (typeof window === "undefined") return;

    const sections = Array.from(document.querySelectorAll(".about-animate"));
    if (!sections.length) return;

    sections.forEach((el, idx) => {
      if (idx % 2 === 1) {
        el.classList.add("from-right");
      }
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
    );

    sections.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [loading, about, homepageCfg, navCfg, team.length, certificates.length, customerReviewCfg]);

  if (loading) {
    return (
      <main
        className="about-page skeleton"
        style={{ "--page-bg": pageBg }}
      >
        <div className="about-hero sk" />
        <div className="container">
          <div className="sk block" style={{ height: 20, width: "30%" }} />
        </div>
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="about-page" style={{ "--page-bg": pageBg }}>
        <div className="container" style={{ padding: "40px 16px" }}>
          <p>{loadError}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="about-page" style={{ "--page-bg": pageBg }}>
      {/* HERO */}
      {aboutHeroSlides.length > 0 && (
        <Hero
          slides={aboutHeroSlides}
          fontStyles={fontStyles}
          autoPlay={about?.useSlideshow ? heroAutoPlay : false}
          intervalMs={heroIntervalMs}
          headingStyleOverride={headingStyle}
          contentStyleOverride={contentStyle}
          buttonStyleOverride={buttonTextStyle}
        />
      )}

      {/* INTRO */}
      {hasIntroContent && (
        <section className="about-intro about-animate">
          <div className="about-intro-grid">
            <div className="about-intro-text">
              {showIntroKicker ? (
                <p className="about-kicker" style={subHeadingStyle}>
                  {introKicker}
                </p>
              ) : null}
              {introHeading ? (
                <h2 className="about-title" style={headingStyle}>
                  {introHeading}
                </h2>
              ) : null}
              <div className="about-copy">
                {introLead && (
                  <p className="about-lead" style={contentStyle}>
                    {introLead}
                  </p>
                )}
                {introBody.map((para, i) => (
                  <p key={i} style={contentStyle}>
                    {para}
                  </p>
                ))}
              </div>
              {primaryCta.text && primaryCta.link ? (
                <div className="about-actions">
                  <a
                    className="about-btn"
                    href={primaryCta.link}
                    style={buttonTextStyle}
                  >
                    {primaryCta.text}
                  </a>
                </div>
              ) : null}
            </div>
            {hasStats ? (
              <div
                className="about-stats-card"
                aria-label="Company statistics"
                ref={statsRef}
              >
                {stats.years ? (
                  <div className="about-stat">
                    <div className="about-stat-num" style={statsStyle}>
                      {yearsNum !== null ? yearsAnimated : stats.years}
                      <span className="plus">+</span>
                    </div>
                    <div className="about-stat-label" style={contentStyle}>
                      Years of Experience
                    </div>
                  </div>
                ) : null}
                {stats.projects ? (
                  <div className="about-stat">
                    <div className="about-stat-num" style={statsStyle}>
                      {projectsNum !== null ? projectsAnimated : stats.projects}
                      <span className="plus">+</span>
                    </div>
                    <div className="about-stat-label" style={contentStyle}>
                      Projects Completed
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>
      )}

      {/* WHY CHOOSE US */}
      {showWhySection ? (
        <section className="about-why about-animate">
          <div className="about-why-shell">
            <div className="about-why-panel">
              <p className="about-why-kicker" style={whyKickerStyle}>
                Why Choose Us
              </p>
              {whyHeading ? (
                <h2 className="about-why-title" style={whyHeadingStyle}>
                  {whyHeading}
                </h2>
              ) : null}
              {whyCopy ? (
                <p className="about-why-copy" style={whyCopyStyle}>
                  {whyCopy}
                </p>
              ) : null}
              {whyButton.text && whyButton.link ? (
                <a
                  className="about-why-btn"
                  href={whyButton.link}
                  style={buttonTextStyle}
                >
                  {whyButton.text}
                </a>
              ) : null}
            </div>

            {whyItems.length ? (
              <div className="about-why-cards">
                {whyItems.map((item, i) => (
                  <div key={i} className="about-why-card">
                    <div className="about-why-card-icon" aria-hidden="true">✦</div>
                    <div>
                      <h3 className="about-why-card-title" style={whyCardTitleStyle}>
                        {item.title}
                      </h3>
                      <p className="about-why-card-text" style={whyCardTextStyle}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* CTA */}
      {showCtaSection ? (
        <section className="about-cta about-animate">
          <div className="about-cta-inner">
            <div>
              {ctaTitle ? (
                <h2 className="about-cta-title" style={ctaHeadingStyle}>
                  {ctaTitle}
                </h2>
              ) : null}
              {ctaCopy ? (
                <p className="about-cta-text" style={ctaCopyStyle}>
                  {ctaCopy}
                </p>
              ) : null}
            </div>
            {ctaButton.text && ctaButton.link ? (
              <a
                className="about-btn light"
                href={ctaButton.link}
                style={buttonTextStyle}
              >
                {ctaButton.text}
              </a>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* CUSTOMER REVIEWS */}
      {customerReviewCfg && (
        <div className="about-animate">
          <CustomerReviews
            config={customerReviewCfg}
            title={customerReviewCfg.sectionTitle}
            subtitle={customerReviewCfg.sectionDescription}
            fontStyles={fontStyles}
          />
        </div>
      )}

      {/* TEAM */}
      {team.length > 0 &&
        team.some((t) => t.name || t.image || t.description) && (
          <section className="team-strip about-animate">
            <div className="container">
              <div className="about-section-head">
                <p className="about-kicker" style={subHeadingStyle}>
                  Our Team
                </p>
                <h2 className="about-title" style={headingStyle}>
                  Designers and project leads
                </h2>
              </div>
              <ul className="team-flex">
                {team.map((m, i) => (
                  <li key={i} className="team-person">
                    <div className="avatar">
                      {m.image ? (
                        <img
                          src={toImageSrc(m.image)}
                          alt={m.name || `Team ${i + 1}`}
                        />
                      ) : (
                        <div className="ph" />
                      )}
                    </div>
                    {m.name && (
                      <h3 className="person-name" style={subHeadingStyle}>
                        {m.name}
                      </h3>
                    )}
                    {m.description && (
                      <p className="person-desc" style={contentStyle}>
                        {m.description}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

      {/* CERTIFICATES */}
      {certificates.length > 0 &&
        certificates.some((c) => c.image || c.description) && (
          <>
            {certificates.length === 1 ? (
              <section className="cert-feature about-animate">
                <div className="cert-feature-inner">
                  <div className="cert-frame">
                    {certificates[0].image ? (
                      <img
                        src={toImageSrc(certificates[0].image)}
                        alt={certificates[0].description || "Certificate"}
                      />
                    ) : (
                      <div className="cert-ph" />
                    )}
                  </div>

                  <div className="cert-copy">
                    <p style={headingStyle}>
                      {certificates[0].description ||
                        "Certified expertise and trusted partners that elevate every project."}
                    </p>
                  </div>
                </div>
              </section>
            ) : (
              <section className="section container about-animate">
                <h2 className="section-title" style={headingStyle}>
                  Certificates
                </h2>
                <ul className="cert-grid">
                  {certificates.map((c, i) => (
                    <li key={i} className="cert-card">
                      <div className="cert-imgwrap">
                        {c.image ? (
                          <img
                            src={toImageSrc(c.image)}
                            alt={c.description || `Certificate ${i + 1}`}
                          />
                        ) : (
                          <div className="cert-ph" />
                        )}
                      </div>
                      {c.description && (
                        <p className="cert-desc" style={contentStyle}>
                          {c.description}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}

      {aboutPdfDocuments.length > 0 && (
        <section className="about-documents about-animate">
          <PdfDocumentSection
            documents={aboutPdfDocuments}
            title="Documents"
            subtitle="Open any PDF card to view full details."
            mode="section"
            styleConfig={pdfConfig?.styleConfig}
          />
        </section>
      )}
    </main>
  );
}
